import { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/App";
import { Button } from "@/components/ui/button";
import {
  LayoutDashboard,
  Users,
  BookOpen,
  Settings,
  LogOut,
  Menu,
  X,
  ChevronRight,
  ClipboardCheck,
  User,
} from "lucide-react";
import falcoLogo from "@/assets/falco.jpeg";

const NAV_ITEMS = {
  student: [
    { label: "Dashboard",   icon: LayoutDashboard, path: "/dashboard" },
    { label: "Practice",    icon: BookOpen,        path: "/practice" },
    { label: "My Attempts", icon: ClipboardCheck,  path: "/attempts" },
    { label: "Profile",     icon: User,            path: "/profile" },
  ],
  sme: [
    { label: "Review Dashboard", icon: LayoutDashboard, path: "/sme/dashboard" },
    { label: "Review Questions", icon: ClipboardCheck,  path: "/sme/review" },
    { label: "Question Bank",    icon: BookOpen,        path: "/sme/questions" },
  ],
  admin: [
    { label: "Admin Dashboard", icon: LayoutDashboard, path: "/admin/dashboard" },
    { label: "Users",           icon: Users,           path: "/admin/users" },
    { label: "Questions",       icon: BookOpen,        path: "/admin/questions" },
    { label: "Settings",        icon: Settings,        path: "/admin/settings" },
  ],
};

const Navigation = ({ role = "student" }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const navItems = NAV_ITEMS[role] || NAV_ITEMS.student;

  const handleLogout = async () => {
    await logout();
    navigate("/login", { replace: true });
  };

  const getRoleBadge = () => {
    switch (role) {
      case "admin": return { label: "Admin",   color: "bg-red-100 text-red-700" };
      case "sme":   return { label: "SME",     color: "bg-purple-100 text-purple-700" };
      default:      return { label: "Student", color: "bg-blue-100 text-blue-700" };
    }
  };

  const badge = getRoleBadge();

  /* ── AnswerCraft logo mark — matches the image font style ── */
  const BrandMark = () => (
    <div className="flex items-center gap-3 px-4 py-3 border-b border-slate-100">
      <img
        src={falcoLogo}
        alt="AnswerCraft"
        className="h-20 w-20 flex-shrink-0 object-cover object-center"
        style={{ borderRadius: "0" }}
      />
      <h1 className="text-xl font-extrabold leading-none tracking-tight whitespace-nowrap">
        <span style={{ color: "#2E3A59" }}>Answer</span><span style={{ color: "#8B6914" }}>Craft</span>
        <sup style={{ color: "#8B6914", fontSize: "0.5em", verticalAlign: "super" }}>™</sup>
      </h1>
    </div>
  );

  /* ── Mobile BrandMark ── */
  const MobileBrandMark = () => (
    <div className="flex items-center gap-2">
      <img src={falcoLogo} alt="AnswerCraft" className="h-10 w-auto rounded-lg flex-shrink-0" />
      <h1 className="text-lg font-extrabold leading-none tracking-tight">
        <span style={{ color: "#2E3A59" }}>Answer</span><span style={{ color: "#8B6914" }}>Craft</span>
        <sup style={{ color: "#8B6914", fontSize: "0.55em", verticalAlign: "super" }}>™</sup>
      </h1>
    </div>
  );

  return (
    <>
      {/* ── DESKTOP SIDEBAR ── */}
      <aside className="hidden lg:flex lg:flex-col lg:w-64 lg:fixed lg:inset-y-0 bg-white border-r border-slate-200">
        <BrandMark />

        {/* Role Badge */}
        <div className="px-6 py-3">
          <span className={`text-xs font-semibold px-3 py-1 rounded-full ${badge.color}`}>
            {badge.label}
          </span>
        </div>

        {/* Nav Items */}
        <nav className="flex-1 px-4 py-4 space-y-1 overflow-y-auto">
          {navItems.map((item) => {
            const isActive =
              location.pathname === item.path ||
              location.pathname.startsWith(item.path + "/");
            return (
              <button
                key={item.path}
                onClick={() => navigate(item.path)}
                className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl text-left transition-all ${
                  isActive ? "bg-[#2E3A59] text-white" : "text-slate-600 hover:bg-slate-100"
                }`}
                data-testid={`nav-${item.label.toLowerCase().replace(/\s/g, "-")}`}
              >
                <item.icon className="w-5 h-5" />
                <span className="font-medium">{item.label}</span>
                {isActive && <ChevronRight className="w-4 h-4 ml-auto" />}
              </button>
            );
          })}
        </nav>

        {/* User Section */}
        <div className="p-4 border-t border-slate-100">
          <div className="flex items-center gap-3 px-3 py-2 mb-3">
            <div className="w-10 h-10 rounded-full bg-[#2E3A59] flex items-center justify-center text-white font-bold">
              {user?.name?.charAt(0) || "U"}
            </div>
            <div className="flex-1 min-w-0">
              <p className="font-medium text-[#2E3A59] truncate">{user?.name}</p>
              <p className="text-xs text-slate-500 truncate">{user?.email}</p>
            </div>
          </div>

          <Button
            variant="ghost"
            onClick={handleLogout}
            className="w-full justify-start text-red-600 hover:text-red-700 hover:bg-red-50"
            data-testid="nav-logout"
          >
            <LogOut className="w-4 h-4 mr-2" /> Log Out
          </Button>
        </div>
      </aside>

      {/* ── MOBILE TOPBAR ── */}
      <div className="lg:hidden fixed top-0 left-0 right-0 z-50 bg-white border-b border-slate-200 px-4 h-20 flex items-center justify-between">
        <MobileBrandMark />
        <button
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          className="p-2 rounded-lg hover:bg-slate-100"
        >
          {mobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
        </button>
      </div>

      {/* ── MOBILE MENU ── */}
      {mobileMenuOpen && (
        <div className="lg:hidden fixed inset-0 z-40 bg-white pt-16">
          <div className="px-4 py-4 space-y-1">
            {navItems.map((item) => {
              const isActive = location.pathname === item.path;
              return (
                <button
                  key={item.path}
                  onClick={() => { navigate(item.path); setMobileMenuOpen(false); }}
                  className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl text-left ${
                    isActive ? "bg-[#2E3A59] text-white" : "text-slate-600 hover:bg-slate-100"
                  }`}
                >
                  <item.icon className="w-5 h-5" />
                  <span className="font-medium">{item.label}</span>
                </button>
              );
            })}
            <button
              onClick={handleLogout}
              className="w-full flex items-center gap-3 px-4 py-3 rounded-xl text-red-600 hover:bg-red-50"
            >
              <LogOut className="w-5 h-5" />
              <span className="font-medium">Log Out</span>
            </button>
          </div>
        </div>
      )}
    </>
  );
};

export default Navigation;